import React, { useState, useEffect } from 'react';
import { Bell, Check, ExternalLink, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '../../lib/api';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  link?: string;
  is_read: boolean;
  created_at: string;
}

/**
 * Otimização de Egress: as notificações agora usam React Query com um único
 * ponto de refresh (polling de 60s). Antes havia 3 gatilhos sobrepostos —
 * `visibilitychange` + `focus` + `setInterval` — que disparavam fetches extras
 * a cada troca de aba/foco, ignorando o cache global do React Query.
 * Os gatilhos de foco/visibility foram REMOVIDOS de propósito.
 */
export const NotificationDropdown: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const { data: notifications = [] } = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: async () => {
      const data = await notificationsApi.list();
      return (data as Notification[]) ?? [];
    },
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 1,
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const unreadNotifications = notifications.filter(n => !n.is_read);

  useEffect(() => {
    if ('setAppBadge' in navigator) {
      if (unreadCount > 0) {
        (navigator as any).setAppBadge(unreadCount).catch(console.error);
      } else {
        (navigator as any).clearAppBadge().catch(console.error);
      }
    }
  }, [unreadCount]);

  const markAsRead = async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      queryClient.setQueryData<Notification[]>(['notifications'], (prev) =>
        prev ? prev.map(n => n.id === id ? { ...n, is_read: true } : n) : prev
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      queryClient.setQueryData<Notification[]>(['notifications'], (prev) =>
        prev ? prev.map(n => ({ ...n, is_read: true })) : prev
      );
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-zinc-400 hover:text-white transition-colors rounded-full hover:bg-zinc-800"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-red-600 text-[10px] font-bold text-white flex items-center justify-center rounded-full border-2 border-zinc-900">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-96 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-900/50">
              <h3 className="font-bold text-white text-sm uppercase tracking-wider">
                {t('console.notifications.title')}
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1 transition-colors"
                >
                  <Check size={12} />
                  {t('console.notifications.mark_all_read')}
                </button>
              )}
            </div>

            <div className="max-h-[480px] overflow-y-auto">
              {unreadNotifications.length === 0 ? (
                <div className="p-8 text-center text-zinc-500 text-sm italic">
                  {t('console.notifications.no_notifications')}
                </div>
              ) : (
                unreadNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="p-4 border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50 transition-colors cursor-pointer group bg-zinc-800/20"
                    onClick={() => {
                      markAsRead(notification.id);
                      if (notification.link) {
                        if (notification.link.startsWith('http://') || notification.link.startsWith('https://')) {
                          window.open(notification.link, '_blank');
                        } else {
                          navigate(notification.link);
                        }
                        setIsOpen(false);
                      }
                    }}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium">
                          {notification.title}
                        </p>
                        <p className="text-xs text-zinc-400 mt-1 leading-relaxed break-words">
                          {notification.message}
                        </p>
                        <span className="text-[10px] text-zinc-600 mt-2 block">
                          {new Date(notification.created_at).toLocaleString()}
                        </span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                        title="Marcar como lida"
                        className="shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-zinc-700 hover:bg-green-700 text-zinc-400 hover:text-white transition-colors"
                      >
                        <Check size={11} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
